import React, { useMemo, useState } from 'react';
import { isEmpty } from 'ramda';
import { useParams } from 'react-router-dom';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { __, sprintf } from '@wordpress/i18n';

// store
import { useStoreValue, storeSet } from '../../../../../../store';

// hooks
import useProjectRole from '../../../../../../hooks/useProjectRole';

// helpers
import getDateFromISOstring from '../../../../../../helpers/getDateFromISOstring';
import { EMPTY_SELECTION, SELECTION_KINDS } from '../../../../../../helpers/selection';
import { getInstrumentLabel, getRecSessionMembers } from '../../../../../../helpers/recSession';

// components
import BadgeTrack from '../../../../../../components/BadgeTrack';
import ButtonIconSimple from '../../../../../../components/ButtonWrapper/components/ButtonIconSimple';
import ButtonIconContextMenu from '../../../../../../components/ButtonWrapper/components/ButtonIconContextMenu';
import ModalUploadSimple from '../../../../../../components/ModalUploadSimple';
import ModalUploadTrack from '../../../../../../components/ModalUploadTrack';
import ModalInviteRecSessionCollaborators from '../../../../../../components/ModalInviteRecSessionCollaborators';
import UserAvatar from '../../../../../../components/UserAvatar';
import ModalDialog from '../../../../../../components/ModalDialog';
import ButtonFlat from '../../../../../../components/ButtonWrapper/components/ButtonFlat';
import ButtonFlatWarning from '../../../../../../components/ButtonWrapper/components/ButtonFlatWarning';
import LikeIcon from '../../../../../../icons/LikeIcon';
import ToReviewIcon from '../../../../../../icons/ToReviewIcon';
import DislikeIcon from '../../../../../../icons/DislikeIcon';
import TripleDotsIcon from '../../../../../../icons/TripleDotsIcon';
import PlusIcon from '../../../../../../icons/PlusIcon';
import SendIcon from '../../../../../../icons/SendIcon';

import '../../../../../../assets/components/RecList.css';

// Remote rooms shown under "ADD REMOTE SESSION"; not bookable yet.
const REMOTE_ROOMS = [
    __('PIANO', 'soundbetween'),
    __('ORCHESTRA', 'soundbetween'),
    __('SYNTH ROOM', 'soundbetween'),
];

/**
 * The REC Session tab of the My Studio left panel.
 *
 * Expanding a session lists its collaborators under the session row. Picking
 * one narrows the timeline to their tracks; picking them again (or nobody)
 * shows every collaborator's. The main track is always on the timeline.
 */
const RecList = () => {
    const { projectId } = useParams();
    const myStudioLeftPanel = useStoreValue('myStudioLeftPanel');
    const recSessions = useStoreValue('recSessions');
    const selectedRecItemId = useStoreValue('selectedRecItemId');
    const selectedRecMemberId = useStoreValue('selectedRecMemberId');
    const currentUserId = useStoreValue('userData')?.id || null;
    const { can, team } = useProjectRole(projectId);

    const [showUploadModal, setShowUploadModal] = useState(false);
    const [inviteFor, setInviteFor] = useState(null); // rec session
    const [uploadTrackModal, setUploadTrackModal] = useState(null); // { recSessionId, type, sessionType, targetLayerId }
    const [confirm, setConfirm] = useState(null); // { header, message, confirmLabel, onConfirm }
    const [confirming, setConfirming] = useState(false);

    const selectedSession = useMemo(
        () => (recSessions || []).find(s => s.id === selectedRecItemId) ?? null,
        [recSessions, selectedRecItemId]
    );

    const members = useMemo(
        () => getRecSessionMembers(selectedSession, team),
        [selectedSession, team]
    );

    // "Aggiungi traccia" uploads into a collaborator's own layer: one's own
    // when invited, otherwise — for whoever runs the session — the layer of
    // the collaborator picked in the list.
    const uploadLayer = useMemo(() => {
        const layers = selectedSession?.auxLayers || [];
        const ownerId = layers.some(l => l.assignedUser?.userId === currentUserId)
            ? currentUserId
            : (can.manageTeam ? selectedRecMemberId : null);

        return ownerId ? layers.find(l => l.assignedUser?.userId === ownerId) ?? null : null;
    }, [selectedSession, currentUserId, selectedRecMemberId, can.manageTeam]);

    const handleSelectItem = (id) => {
        storeSet('selectedRecItemId', id);
        storeSet('selectedRecMemberId', null);
        // A rec session expands one at a time, so the shared selection — what the
        // Comm Center scopes its feed to — holds that one session.
        storeSet('selection', id
            ? { kind: SELECTION_KINDS.REC, ids: [id], anchor: id }
            : EMPTY_SELECTION);
    };

    const handleSelectMember = (userId) => {
        storeSet('selectedRecMemberId', selectedRecMemberId === userId ? null : userId);
    };

    const openUploadForLayer = (session, layer) => {
        setUploadTrackModal({
            recSessionId: session.id,
            type: 'AUX',
            sessionType: layer.sessionType || 'OTHER',
            targetLayerId: layer.id,
        });
    };

    // Both of these throw away audio, so they ask first.
    const getSessionMenuItems = (session) => {
        const items = [];

        if (session.coreLayer?.tracks?.length) {
            items.push({
                label: __('Rimuovi traccia principale', 'soundbetween'),
                onClick: () => setConfirm({
                    header: __('Rimuovi traccia principale', 'soundbetween'),
                    message: __('La traccia principale verrà tolta dalla sessione. Le tracce dei collaboratori restano.', 'soundbetween'),
                    confirmLabel: __('Rimuovi', 'soundbetween'),
                    onConfirm: () => storeSet('removeRecSessionMainTrack', projectId, session.id),
                }),
            });
        }

        items.push({
            label: __('Elimina sessione', 'soundbetween'),
            onClick: () => setConfirm({
                header: __('Elimina sessione', 'soundbetween'),
                message: sprintf(
                    __('"%s" verrà eliminata con la traccia principale e le tracce di tutti i collaboratori.', 'soundbetween'),
                    session.title || session.label
                ),
                confirmLabel: __('Elimina', 'soundbetween'),
                onConfirm: () => storeSet('deleteRecSession', projectId, session.id),
            }),
        });

        return items;
    };

    const handleConfirm = async () => {
        if (!confirm || confirming) return;
        setConfirming(true);
        try {
            await confirm.onConfirm();
            setConfirm(null);
        } finally {
            setConfirming(false);
        }
    };

    const renderMembers = (session) => {
        if (members.length === 0) return null;

        return (
            <ul className="recList__members">
                {members.map(member => {
                    const isPicked = member.userId === selectedRecMemberId;
                    const instruments = member.instruments.map(getInstrumentLabel).join(', ');
                    const menuItems = can.manageTeam && member.trackCount === 0
                        ? [{
                            label: __('Rimuovi dalla sessione', 'soundbetween'),
                            onClick: () => storeSet('removeRecSessionMember', projectId, session.id, member.userId),
                        }]
                        : [];

                    return (
                        <li
                            key={member.userId}
                            className={[
                                'recList__member',
                                isPicked ? 'recList__member--picked' : '',
                                member.pending ? 'recList__member--pending' : '',
                            ].filter(Boolean).join(' ')}
                        >
                            <button
                                type="button"
                                className="recList__memberBtn"
                                aria-pressed={isPicked}
                                title={isPicked
                                    ? __('Mostra le tracce di tutti i collaboratori', 'soundbetween')
                                    : __('Mostra solo le tracce di questo collaboratore', 'soundbetween')}
                                onClick={() => handleSelectMember(member.userId)}
                            >
                                <UserAvatar url={member.profilePictureUrl} name={member.fullName}/>
                                <span className="recList__memberText">
                                    <span className="recList__memberName">{member.fullName}</span>
                                    <span className="recList__memberMeta">
                                        {member.pending
                                            ? __('Invito in attesa', 'soundbetween')
                                            : instruments}
                                    </span>
                                </span>
                            </button>
                            {menuItems.length > 0 && (
                                <ButtonIconContextMenu
                                    menuItems={menuItems}
                                    ariaLabel={__('Opzioni collaboratore', 'soundbetween')}
                                >
                                    <TripleDotsIcon/>
                                </ButtonIconContextMenu>
                            )}
                        </li>
                    );
                })}
            </ul>
        );
    };

    const renderSessionBody = (session) => (
        <div className="recList__sessionBody" onClick={e => e.stopPropagation()}>
            {renderMembers(session)}

            {can.manageTeam && (
                <button
                    type="button"
                    className="recList__action"
                    onClick={() => setUploadTrackModal({ recSessionId: session.id, type: 'CORE', sessionType: null })}
                >
                    <PlusIcon/>
                    <span>{__('Aggiungi traccia principale', 'soundbetween')}</span>
                </button>
            )}

            {uploadLayer && (
                <button
                    type="button"
                    className="recList__action"
                    onClick={() => openUploadForLayer(session, uploadLayer)}
                >
                    <PlusIcon/>
                    <span>{__('Aggiungi traccia', 'soundbetween')}</span>
                </button>
            )}

            <div className="recList__action recList__action--remote">
                <div className="recList__actionRow">
                    <PlusIcon/>
                    <span>{__('ADD REMOTE SESSION', 'soundbetween')}</span>
                </div>
                <div className="recList__remoteRooms">
                    {REMOTE_ROOMS.map(room => (
                        <button
                            key={room}
                            type="button"
                            className="recList__remoteRoom"
                            disabled
                            title={__('Presto disponibile', 'soundbetween')}
                        >
                            {room}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        myStudioLeftPanel === 'rec'
            ? <>
                <OverlayScrollbarsComponent element="div" options={{ scrollbars: { autoHide: 'never' } }} defer>
                    <ul className="timelineEditorAuxControls__listOfTracks">
                        {recSessions && !isEmpty(recSessions)
                            ? recSessions.map(item => {
                                const isSelected = item.id === selectedRecItemId;
                                return (
                                    <li
                                        key={item.id}
                                        className={`recList__item${isSelected ? ' recList__item--selected' : ''}`}
                                    >
                                        <div
                                            className="recList__itemRow"
                                            onClick={() => handleSelectItem(isSelected ? null : item.id)}
                                        >
                                            <div>
                                                <BadgeTrack>{item.label}</BadgeTrack>
                                            </div>
                                            <div className="title">
                                                <h3>{item.title}</h3>
                                                <span>{item.timecode}</span>
                                            </div>
                                            <div className="rating lastGroup">
                                                <ButtonIconSimple onClick={e => e.stopPropagation()}>
                                                    <LikeIcon/>
                                                </ButtonIconSimple>
                                                <ButtonIconSimple onClick={e => e.stopPropagation()}>
                                                    <ToReviewIcon/>
                                                </ButtonIconSimple>
                                                <ButtonIconSimple onClick={e => e.stopPropagation()}>
                                                    <DislikeIcon/>
                                                </ButtonIconSimple>
                                            </div>
                                            <div className="meta">
                                                <span>{getDateFromISOstring(item.date, {
                                                    day: '2-digit',
                                                    month: 'short'
                                                })}</span>
                                                {can.manageTeam && (
                                                    <div className="recList__rowActions" onClick={e => e.stopPropagation()}>
                                                        <button
                                                            type="button"
                                                            className="recList__invite"
                                                            title={__('Invita collaboratori', 'soundbetween')}
                                                            aria-label={__('Invita collaboratori', 'soundbetween')}
                                                            onClick={() => {
                                                                if (!isSelected) handleSelectItem(item.id);
                                                                setInviteFor(item);
                                                            }}
                                                        >
                                                            <SendIcon/>
                                                        </button>
                                                        <ButtonIconContextMenu
                                                            menuItems={getSessionMenuItems(item)}
                                                            ariaLabel={__('Opzioni sessione', 'soundbetween')}
                                                        >
                                                            <TripleDotsIcon/>
                                                        </ButtonIconContextMenu>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {isSelected && renderSessionBody(item)}
                                    </li>
                                );
                            }) : null}
                    </ul>
                </OverlayScrollbarsComponent>
                <button
                    className="timelineEditorAuxControls__addNew"
                    onClick={() => setShowUploadModal(true)}
                    title={__('Nuova sessione di registrazione', 'soundbetween')}
                    aria-label={__('Nuova sessione di registrazione', 'soundbetween')}
                >
                    <PlusIcon/>
                </button>
                {showUploadModal && (
                    <ModalUploadSimple
                        projectId={projectId}
                        type="rec"
                        onCreated={session => handleSelectItem(session.id)}
                        onClose={() => setShowUploadModal(false)}
                    />
                )}
                {inviteFor && (
                    <ModalInviteRecSessionCollaborators
                        projectId={projectId}
                        recSession={(recSessions || []).find(s => s.id === inviteFor.id) ?? inviteFor}
                        onClose={() => setInviteFor(null)}
                    />
                )}
                {confirm && (
                    <ModalDialog
                        header={confirm.header}
                        visible={true}
                        width={420}
                        onHide={() => !confirming && setConfirm(null)}
                    >
                        <p className="recList__confirmText">{confirm.message}</p>
                        <div className="appFormActions">
                            <ButtonFlat size="lg" onClick={() => setConfirm(null)} disabled={confirming}>
                                {__('Annulla', 'soundbetween')}
                            </ButtonFlat>
                            <ButtonFlatWarning size="lg" onClick={handleConfirm} disabled={confirming}>
                                {confirm.confirmLabel}
                            </ButtonFlatWarning>
                        </div>
                    </ModalDialog>
                )}
                {uploadTrackModal && (
                    <ModalUploadTrack
                        projectId={projectId}
                        recSessionId={uploadTrackModal.recSessionId}
                        type={uploadTrackModal.type}
                        sessionType={uploadTrackModal.sessionType}
                        targetLayerId={uploadTrackModal.targetLayerId}
                        onClose={() => setUploadTrackModal(null)}
                    />
                )}
            </> : null
    );
};

export default RecList;
